import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { toMediaSrc } from "../utils/mediaSrc";

type MediaKind = "product" | "color";

type AuthMediaImgProps = {
  kind: MediaKind;
  resourceId?: string | number | null;
  accountId: string;
  authToken: string;
  /** Inline base64 / data URI / http URL — preferred when already available */
  inlineSrc?: string | null;
  /** When false, skip network fetch (e.g. hasImage=false) */
  hasImage?: boolean;
  alt?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
  fallback?: React.ReactNode;
};

const blobCache = new Map<string, string>();

function cacheKey(kind: MediaKind, accountId: string, resourceId: string) {
  return `${kind}:${accountId}:${resourceId}`;
}

/**
 * Renders product/color images via authenticated blob fetch so list APIs can stay lite
 * (no multi-MB base64). Falls back to inlineSrc when provided.
 */
export const AuthMediaImg: React.FC<AuthMediaImgProps> = ({
  kind,
  resourceId,
  accountId,
  authToken,
  inlineSrc,
  hasImage = true,
  alt = "",
  className,
  onClick,
  fallback = null,
}) => {
  const inline = toMediaSrc(inlineSrc);
  const [src, setSrc] = useState<string>(inline);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (inline) {
      setSrc(inline);
      return;
    }
    const id = String(resourceId ?? "").trim();
    if (!hasImage || !id || !accountId || !authToken) {
      setSrc("");
      return;
    }
    const key = cacheKey(kind, accountId, id);
    const cached = blobCache.get(key);
    if (cached) {
      setSrc(cached);
      return;
    }

    let cancelled = false;
    const path =
      kind === "product"
        ? `account/${accountId}/product/${id}/image`
        : `account/${accountId}/color/${id}/image`;

    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}${path}`, {
          headers: { Authorization: `Bearer ${authToken}` },
          responseType: "blob",
        });
        if (cancelled) return;
        if (!res.data || (res.data as Blob).size === 0) {
          setSrc("");
          setFailed(true);
          return;
        }
        const url = URL.createObjectURL(res.data);
        blobCache.set(key, url);
        setSrc(url);
      } catch {
        if (!cancelled) {
          setSrc("");
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inline, kind, resourceId, accountId, authToken, hasImage]);

  if (!src || failed) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={() => setFailed(true)}
    />
  );
};
